import { useQuery } from '@tanstack/react-query';
import * as gitlabApi from '@/api/gitlab.api';

/**
 * Query keys for commits
 */
export const commitKeys = {
  all: ['commits'] as const,
  lists: () => [...commitKeys.all, 'list'] as const,
  list: (params: gitlabApi.CommitQueryParams) => [...commitKeys.lists(), params] as const,
  recent: (limit: number) => [...commitKeys.all, 'recent', limit] as const,
  stats: (params?: Record<string, unknown>) => [...commitKeys.all, 'stats', params] as const,
  byUser: (userId: string, startDate?: string, endDate?: string) =>
    [...commitKeys.all, 'byUser', userId, { startDate, endDate }] as const,
  userActivity: (userId: string, days: number) =>
    [...commitKeys.all, 'userActivity', userId, days] as const,
  detail: (id: string) => [...commitKeys.all, 'detail', id] as const,
};

/**
 * Fetch commits with filters
 */
export function useCommits(params?: gitlabApi.CommitQueryParams) {
  return useQuery({
    queryKey: commitKeys.list(params || {}),
    queryFn: () => gitlabApi.getCommits(params),
  });
}

/**
 * Fetch recent commits
 */
export function useRecentCommits(limit: number = 20) {
  return useQuery({
    queryKey: commitKeys.recent(limit),
    queryFn: () => gitlabApi.getRecentCommits(limit),
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Fetch commit statistics
 */
export function useCommitStats(params?: {
  repoId?: string;
  userId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: commitKeys.stats(params),
    queryFn: () => gitlabApi.getCommitStats(params),
  });
}

/**
 * Fetch commits by user
 */
export function useCommitsByUser(
  userId: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: commitKeys.byUser(userId, startDate, endDate),
    queryFn: () => gitlabApi.getCommitsByUser(userId, startDate, endDate),
    enabled: !!userId,
  });
}

/**
 * Fetch user commit activity
 */
export function useUserActivity(userId: string, days: number = 30) {
  return useQuery({
    queryKey: commitKeys.userActivity(userId, days),
    queryFn: () => gitlabApi.getUserActivity(userId, days),
    enabled: !!userId,
  });
}

/**
 * Fetch a specific commit
 */
export function useCommit(id: string) {
  return useQuery({
    queryKey: commitKeys.detail(id),
    queryFn: () => gitlabApi.getCommit(id),
    enabled: !!id,
  });
}

/**
 * Fetch commits by repository
 */
export function useCommitsByRepo(repoId: string, limit: number = 50) {
  return useQuery({
    queryKey: [...commitKeys.all, 'byRepo', repoId, limit] as const,
    queryFn: () => gitlabApi.getCommitsByRepo(repoId, limit),
    enabled: !!repoId,
  });
}
